import { supabase } from '../lib/supabase';
import { captureError } from '../lib/sentry';
import { logAudit, AUDIT_ACTIONS } from './auditService';
import { safeQuery } from '../utils/safeSupabase';

// Phase 11 slot 4 (2026-04-24) — extends GDPR coverage from Articles 15/17/20
// (migrations 057-059 backend) to Articles 16 (rectification) + 18 (restriction)
// + Art 17 soft-delete fulfillment (migration 258).

/**
 * GDPR Article 17/20 data-lifecycle service.
 *
 * Article 20 (portability): `exportOrgData` calls a SECURITY DEFINER RPC
 * that returns a JSONB bundle with all org-scoped data, PII-sanitized.
 * The caller downloads the bundle as a timestamped `.json` file.
 *
 * Article 17 (erasure): `requestAccountDeletion` / `cancelDeletionRequest`
 * write to the `deletion_request` queue. An SPL processes the cascade via
 * the runbook at `docs/runbooks/gdpr.md`. No hard-delete happens client-side.
 *
 * All three actions are audited server-side by the RPC. Client-side audit
 * calls (AUDIT_ACTIONS.*) are a belt-and-suspenders safety net in case the
 * RPC insert fails — duplicate entries are harmless since they carry
 * different timestamps and the audit log is append-only anyway.
 */

/**
 * Article 15 (right of access) — exports the calling user's own data.
 * Zero-param; the RPC uses auth.uid() as the subject.
 * Returns the JSONB bundle; caller serializes via downloadExportBundle().
 */
export async function exportMyData() {
  const { data, error } = await supabase.rpc('export_my_data');
  if (error) {
    captureError?.(error, { location: 'gdprService.exportMyData' });
    throw error;
  }
  return data;
}

export async function exportOrgData(orgId) {
  if (!orgId) throw new Error('exportOrgData: orgId required');

  const { data, error } = await supabase.rpc('export_org_data', { p_org_id: orgId });
  if (error) {
    captureError?.(error, { location: 'gdprService.exportOrgData', orgId });
    throw error;
  }

  logAudit(AUDIT_ACTIONS.EXPORT_ORG_DATA, {
    entityType: 'organization',
    entityId: orgId,
    details: {
      user_count: data?.users?.length ?? 0,
      job_count: data?.jobs?.length ?? 0,
    },
  });

  return data;
}

/**
 * Trigger a browser download of the export as a JSON file. Separate from
 * exportOrgData() so callers can also render the bundle in-page if needed.
 */
export function downloadExportBundle(bundle, orgName) {
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const safeName = (orgName || 'org').replace(/[^a-z0-9-]+/gi, '_').toLowerCase();
  const filename = `juuni-export-${safeName}-${ts}.json`;
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
}

export async function requestAccountDeletion({ reason = null, scope = 'account' } = {}) {
  const { data, error } = await supabase.rpc('request_account_deletion', {
    p_reason: reason,
    p_scope: scope,
  });
  if (error) {
    captureError?.(error, { location: 'gdprService.requestAccountDeletion', scope });
    throw error;
  }
  return data;
}

export async function cancelDeletionRequest(requestId) {
  if (!requestId) throw new Error('cancelDeletionRequest: requestId required');
  const { data, error } = await supabase.rpc('cancel_deletion_request', { p_id: requestId });
  if (error) {
    captureError?.(error, { location: 'gdprService.cancelDeletionRequest', requestId });
    throw error;
  }
  return data;
}

/**
 * Admin-facing listing. RLS scopes the SELECT to the caller's org, so no
 * explicit org_id filter needed.
 */
export async function listDeletionRequests({ includeResolved = false } = {}) {
  let query = supabase.from('deletion_request').select('*').order('requested_at', { ascending: false });
  if (!includeResolved) query = query.eq('status', 'pending');
  const { data, error } = await query;
  if (error) {
    captureError?.(error, { location: 'gdprService.listDeletionRequests' });
    throw error;
  }
  return data || [];
}

/**
 * User-facing self-lookup. Returns the current pending request for this
 * user, or null. Used to toggle Request vs Cancel affordance.
 */
export async function getOwnPendingDeletionRequest() {
  const { data, error } = await supabase
    .from('deletion_request')
    .select('*')
    .eq('status', 'pending')
    .order('requested_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    captureError?.(error, { location: 'gdprService.getOwnPendingDeletionRequest' });
    return null;
  }
  return data;
}

// ─── Phase 11 slot 4 — Article 16 (rectification) ──────────────────

/**
 * User submits a rectification request for a personal-data field.
 * Backed by `request_rectification(p_field, p_current, p_requested, p_reason)`
 * RPC (migration 258). Audit-logged server-side.
 *
 * Allowed fields are enforced server-side; if the field isn't on the
 * allowlist, the RPC raises `feature_not_supported`. UI should constrain
 * the dropdown to the allowed set.
 */
export async function requestRectification({ fieldName, currentValue, requestedValue, reason = null }) {
  const { data, error } = await supabase.rpc('request_rectification', {
    p_field_name:      fieldName,
    p_current_value:   currentValue ?? null,
    p_requested_value: requestedValue,
    p_reason:          reason,
  });
  if (error) {
    captureError?.(error, { location: 'gdprService.requestRectification', fieldName });
    throw error;
  }
  logAudit(AUDIT_ACTIONS.GDPR_RECTIFICATION_REQUESTED, {
    entityType: 'rectification_request',
    entityId:   data?.request_id,
    details:    { field_name: fieldName, has_reason: !!reason },
  });
  return data;
}

/**
 * Admin processes a pending rectification request. action = 'approve' applies
 * the change to the user's `individual` row; 'reject' records the rationale.
 */
export async function processRectificationRequest({ requestId, action, notes = null }) {
  const { data, error } = await supabase.rpc('process_rectification_request', {
    p_request_id: requestId,
    p_action:     action,
    p_notes:      notes,
  });
  if (error) {
    captureError?.(error, { location: 'gdprService.processRectificationRequest', requestId, action });
    throw error;
  }
  return data;
}

/** User-facing list of own rectification requests. RLS scopes to self. */
export async function listOwnRectificationRequests() {
  const { data, error } = await supabase
    .from('rectification_request')
    .select('*')
    .order('requested_at', { ascending: false });
  if (error) { captureError?.(error, { location: 'gdprService.listOwnRectificationRequests' }); return []; }
  return data || [];
}

/** Admin-facing list. RLS scopes to caller's org. */
export async function listRectificationRequests({ includeResolved = false } = {}) {
  let q = supabase.from('rectification_request').select('*').order('requested_at', { ascending: false });
  if (!includeResolved) q = q.eq('status', 'pending');
  const { data, error } = await q;
  if (error) { captureError?.(error, { location: 'gdprService.listRectificationRequests' }); return []; }
  return data || [];
}

// ─── Phase 11 slot 4 — Article 17 fulfillment (admin-side) ─────────

/**
 * Admin processes a pending deletion request. action = 'complete' triggers
 * the soft-delete (PII anonymization) cascade via `process_deletion_request`
 * RPC. action = 'reject' marks cancelled with admin notes.
 */
export async function processDeletionRequest({ requestId, action, notes = null }) {
  const { data, error } = await supabase.rpc('process_deletion_request', {
    p_request_id: requestId,
    p_action:     action,
    p_notes:      notes,
  });
  if (error) {
    captureError?.(error, { location: 'gdprService.processDeletionRequest', requestId, action });
    throw error;
  }
  return data;
}

// ─── Phase 11 slot 4 — Article 18 (restriction of processing) ─────

/**
 * User invokes Art 18 restriction. Idempotent — re-calling with a new reason
 * updates the reason but doesn't re-stamp the timestamp.
 */
export async function requestProcessingRestriction({ reason = null } = {}) {
  const { data, error } = await supabase.rpc('request_processing_restriction', {
    p_reason: reason,
  });
  if (error) {
    captureError?.(error, { location: 'gdprService.requestProcessingRestriction' });
    throw error;
  }
  logAudit(AUDIT_ACTIONS.GDPR_RESTRICTION_REQUESTED, {
    entityType: 'individual',
    entityId:   data?.user_id,
    details:    { has_reason: !!reason },
  });
  return data;
}

/** Admin lifts an Art 18 restriction (e.g., after dispute resolution). */
export async function liftProcessingRestriction({ userId, notes = null }) {
  const { data, error } = await supabase.rpc('lift_processing_restriction', {
    p_user_id: userId,
    p_notes:   notes,
  });
  if (error) {
    captureError?.(error, { location: 'gdprService.liftProcessingRestriction', userId });
    throw error;
  }
  return data;
}

/** Admin-facing list of currently-restricted users. */
export async function listRestrictedUsers() {
  const { data, error } = await safeQuery(
    supabase
      .from('individual')
      .select('id, name, email, processing_restricted_at, processing_restriction_reason')
      .not('processing_restricted_at', 'is', null)
      .order('processing_restricted_at', { ascending: false }),
    'gdpr-listRestrictedUsers',
  );
  if (error) { captureError?.(error, { location: 'gdprService.listRestrictedUsers' }); return []; }
  return data || [];
}

/** Returns true if the calling user has an active restriction (UI hint). */
export async function getOwnProcessingRestriction() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) return null;
  const { data, error } = await supabase
    .from('individual')
    .select('processing_restricted_at, processing_restriction_reason')
    .eq('id', user.id)
    .maybeSingle();
  if (error) { captureError?.(error, { location: 'gdprService.getOwnProcessingRestriction' }); return null; }
  if (!data?.processing_restricted_at) return null;
  return data;
}
